import { Card, CardContent, Link, TableCell, Typography } from "@mui/material";
import type { Column } from "~/components/Table";
import {
  getActiveFiltersCount,
  makeRequestSchema,
  prepareListRequest,
  ResourceTable,
  useSearchRequest,
  withoutBaseTableModel,
  withSearchValidationErrorBoundary,
} from "~/components/Table";
import { auditColumns } from "~/domain/auditable";
import { useTopics } from "~/domain/datastores";
import {
  DataStoreLink,
  makeNewTopicLocation,
  makeTopicLocation,
} from "~/paths";
import type { Topic } from "~/services/datastore";
import { listTopicsSchema } from "../schemas";
import { TopicFilters } from "./TopicFilters";

const COLUMNS: ReadonlyArray<Column<Topic>> = [
  {
    header: "Topic",
    renderCell(topic) {
      return (
        <TableCell>
          {topic.name}
          <Link
            component={DataStoreLink}
            to={makeTopicLocation({ topicId: topic.id })}
          >
            <Typography variant="body2">{topic.id}</Typography>
          </Link>
        </TableCell>
      );
    },
  },
  {
    accessor: "associatedTopicId",
    dataType: "foreign-key",
    resourceType: "topic",
    defaultHidden: true,
  },
  {
    accessor: "logId",
    dataType: "foreign-key",
    resourceType: "log",
  },
  {
    accessor: "typeName",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "typeEncoding",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "startTime",
    dataType: "timestamp",
    isSortable: true,
  },
  {
    accessor: "endTime",
    dataType: "timestamp",
    isSortable: true,
  },
  {
    accessor: "recordSize",
    dataType: "bytes",
    isSortable: true,
  },
  {
    accessor: "recordCount",
    dataType: "number",
    isSortable: true,
  },
  {
    accessor: "objectSize",
    dataType: "bytes",
    isSortable: true,
  },
  {
    accessor: "objectCount",
    dataType: "number",
    isSortable: true,
  },
  {
    accessor: "locked",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "lockedBy",
    dataType: "foreign-key",
    resourceType: "user",
    defaultHidden: true,
  },
  {
    accessor: "lockedAt",
    dataType: "datetime",
    isSortable: true,
    defaultHidden: true,
  },
  ...auditColumns,
];

const requestSchema = makeRequestSchema(COLUMNS, listTopicsSchema);

export const TopicTable = withSearchValidationErrorBoundary(
  function TopicTable() {
    const [request, setRequest] = useSearchRequest(requestSchema);

    const searchQuery = useTopics(prepareListRequest(request), {
      keepPreviousData: true,
      cacheTime: 0,
    });

    const filterValues = withoutBaseTableModel(request);

    return (
      <Card>
        <CardContent>
          <ResourceTable
            resourceName="topic"
            resourceCreateLocation={makeNewTopicLocation()}
            getRowKey={(topic) => topic.id}
            columns={COLUMNS}
            searchQuery={searchQuery}
            tableModel={request}
            onTableModelChange={setRequest}
            filterSection={
              <TopicFilters values={filterValues} setValues={setRequest} />
            }
            activeFilterCount={getActiveFiltersCount(filterValues)}
          />
        </CardContent>
      </Card>
    );
  },
);
